import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError, uploadPhoto } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES } from "@/lib/constants";
import { ArrowLeft, Upload, Loader2, X, Leaf } from "lucide-react";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";

export default function PostJob() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]); // cloud storage urls
  const [uploading, setUploading] = useState(false);
  const [pickupCoords, setPickupCoords] = useState({ lat: null, lng: null });

  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "waste_removal",
    pickup_address: "",
    dropoff_address: "",
    budget: "",
    size_estimate: "medium",
    dimensions: "",
    preferred_date: "",
    preferred_time: "flexible",
    bike_friendly: false,
  });

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      if (file.size > 8_000_000) { toast.error(`${file.name} er for stor (max 8MB)`); continue; }
      try {
        const url = await uploadPhoto(file);
        setPhotos(p => [...p, url]);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || `Kunne ikke uploade ${file.name}`);
      }
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/jobs", {
        ...form,
        budget: parseFloat(form.budget),
        photos,
        pickup_lat: pickupCoords.lat,
        pickup_lng: pickupCoords.lng,
      });
      toast.success("Job oprettet!");
      navigate(`/jobs/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kunne ikke oprette job");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Tilbage
      </Link>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Opret et job</CardTitle>
          <p className="text-sm text-muted-foreground">Beskriv hvad der skal afhentes eller flyttes</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-5">
            <Field label="Jobtitel *">
              <Input required value={form.title} onChange={(e) => upd("title", e.target.value)} placeholder="f.eks. Fjernelse af gammel sofa" data-testid="post-title" />
            </Field>

            <Field label="Kategori *">
              <Select value={form.category} onValueChange={(v) => upd("category", v)}>
                <SelectTrigger data-testid="post-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>

            <Field label="Beskrivelse">
              <Textarea rows={4} value={form.description} onChange={(e) => upd("description", e.target.value)} placeholder="Beskriv genstandene, stand og eventuelle særlige instruktioner..." data-testid="post-description" />
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Afhentningsadresse *">
                <AddressAutocomplete
                  value={form.pickup_address}
                  onChange={(v) => { upd("pickup_address", v); setPickupCoords({ lat: null, lng: null }); }}
                  onSelect={(addr, lat, lng) => { upd("pickup_address", addr); setPickupCoords({ lat, lng }); }}
                  placeholder="Begynd at skrive adressen..."
                  required
                  testId="post-pickup"
                />
              </Field>
              <Field label="Afleveringsadresse">
                <AddressAutocomplete
                  value={form.dropoff_address}
                  onChange={(v) => upd("dropoff_address", v)}
                  onSelect={(addr) => upd("dropoff_address", addr)}
                  placeholder="Valgfri"
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Budget (kr.) *">
                <Input required type="number" min="1" value={form.budget} onChange={(e) => upd("budget", e.target.value)} placeholder="350" data-testid="post-budget" />
              </Field>
              <Field label="Størrelse">
                <Select value={form.size_estimate} onValueChange={(v) => upd("size_estimate", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Lille</SelectItem>
                    <SelectItem value="medium">Mellem</SelectItem>
                    <SelectItem value="large">Stor</SelectItem>
                    <SelectItem value="extra_large">Ekstra stor</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Mål/dimensioner">
              <Input value={form.dimensions} onChange={(e) => upd("dimensions", e.target.value)} placeholder="f.eks. Sofa 200x80cm" />
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Foretrukket tidspunkt">
                <Select value={form.preferred_time} onValueChange={(v) => upd("preferred_time", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">Morgen</SelectItem>
                    <SelectItem value="afternoon">Eftermiddag</SelectItem>
                    <SelectItem value="evening">Aften</SelectItem>
                    <SelectItem value="flexible">Fleksibelt</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Foretrukket dato">
                <Input type="date" value={form.preferred_date} onChange={(e) => upd("preferred_date", e.target.value)} />
              </Field>
            </div>

            <Field label="Fotos">
              <div className="flex flex-wrap gap-3">
                {photos.map((p, i) => (
                  <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-border group">
                    <img src={p} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-5 h-5 bg-background/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 rounded-xl border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors" data-testid="post-photo-upload">
                  {uploading ? <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" /> : <Upload className="w-5 h-5 text-muted-foreground" />}
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handleFile} disabled={uploading} />
                </label>
              </div>
            </Field>

            <label className="flex items-start gap-2.5 p-3 rounded-xl bg-emerald-50 border border-emerald-200 cursor-pointer hover:bg-emerald-100/60 transition-colors">
              <input
                type="checkbox"
                checked={form.bike_friendly}
                onChange={(e) => upd("bike_friendly", e.target.checked)}
                className="mt-0.5 accent-emerald-600"
                data-testid="bike-friendly-checkbox"
              />
              <div className="text-sm">
                <p className="font-medium flex items-center gap-1.5 text-emerald-900"><Leaf className="w-4 h-4 text-emerald-600" /> Kan hentes på ladcykel</p>
                <p className="text-xs text-emerald-800/70 mt-0.5">Lette/mindre ting? Markér jobbet, så cykel-chauffører nemt finder det, grønt og ofte billigere.</p>
              </div>
            </label>

            <Button type="submit" size="lg" className="w-full rounded-xl font-semibold" disabled={loading || uploading} data-testid="post-submit">
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Opret job
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
